import {
  ContrastScope,
  Css,
  IconButton,
  ScrollableParent,
  SideNav,
  SideNavLayoutProvider,
  SideNavProps,
  useBreakpoint,
  useSideNavLayoutContext,
  useTestIds,
  zIndices,
} from "@homebound/beam";
import { PropsWithChildren, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { topNavHeight } from "src/routes/layout/global-nav/GlobalNav";

const railCollapsedWidthPx = 56;

type BeamSideNavViewportLayoutProps = {
  sideNav: SideNavProps;
  railWidthPx?: number;
  contrastRail?: boolean;
  layoutProps?: { contentCss: Parameters<typeof ScrollableParent>["0"]["xss"] };
};

/**
 * Temporary: Beam SideNav without SideNavLayout, so pages can migrate one-by-one.
 * Beam's SideNavLayout assumes document scroll and NavbarLayout; project pages still
 * scroll in a viewport `ScrollableParent` under GlobalNav. This copies Beam's rail
 * chrome but keeps the legacy overlay, mdAndDown auto-close, and viewport scroll so
 * existing pages don't change until they can opt into SideNavLayout.
 */
export function BeamSideNavViewportLayout(props: PropsWithChildren<BeamSideNavViewportLayoutProps>) {
  return (
    <SideNavLayoutProvider>
      <BeamSideNavViewportLayoutContent {...props} />
    </SideNavLayoutProvider>
  );
}

function BeamSideNavViewportLayoutContent(props: PropsWithChildren<BeamSideNavViewportLayoutProps>) {
  const { children, sideNav, railWidthPx = 260, contrastRail = false, layoutProps } = props;
  const { navState, setNavState } = useSideNavLayoutContext();
  const bp = useBreakpoint();
  const tid = useTestIds({}, "sideNavLayout");
  const location = useLocation();

  const collapsed = navState === "collapse";
  const expanded = navState === "expanded";
  const showRail = navState !== "hidden";

  useEffect(() => {
    if (expanded && bp.mdAndDown) {
      setNavState("collapse");
    }
    // Omit `expanded` so opening the nav doesn't immediately close it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, bp.mdAndDown, setNavState]);

  const rail = showRail && (
    <div
      css={{
        ...Css.df.fdc.fs0.oh.relative.bgWhite.bcGray200.transition.br
          .wPx(collapsed ? railCollapsedWidthPx : railWidthPx)
          .z(zIndices.sideNav).$,
        ...(bp.mdAndDown ? Css.absolute.h(`calc(100% - ${topNavHeight}px)`).$ : {}),
      }}
      {...tid}
    >
      <div css={Css.absolute.right2.top2.z2.$}>
        <IconButton
          icon={collapsed ? "menuOpen" : "menuClose"}
          label={collapsed ? "Expand navigation" : "Collapse navigation"}
          onClick={() => setNavState(collapsed ? "expanded" : "collapse")}
          {...tid.toggle}
        />
      </div>
      <div css={Css.fg1.mh0.df.fdc.$}>
        <SideNav
          {...sideNav}
          // Items have no icons; Beam would hide the list and leave top/footer in an empty rail.
          top={expanded ? sideNav.top : undefined}
          footer={expanded ? sideNav.footer : undefined}
        />
      </div>
    </div>
  );

  return (
    <div css={Css.df.oh.fg1.$}>
      {contrastRail ? <ContrastScope>{rail}</ContrastScope> : rail}
      {expanded && bp.mdAndDown && (
        <div
          css={
            Css.absolute.right0.bottom0.left0
              .topPx(topNavHeight)
              .add("backgroundColor", "rgba(36,36,36,0.2)")
              .add("zIndex", zIndices.sideNav - 1).$
          }
          onClick={() => setNavState("collapse")}
          aria-label="Close navigation"
        />
      )}
      <ScrollableParent
        px={3}
        xss={{
          ...Css.fg1.$,
          ...(bp.mdAndDown && showRail ? Css.mlPx(railCollapsedWidthPx).$ : {}),
          ...(layoutProps?.contentCss || {}),
        }}
      >
        {children}
      </ScrollableParent>
    </div>
  );
}
